import { isNaN, parseInt } from 'lodash';
// needs to have this full import path otherwise transitive imports will break DateFormatter.test.ts
import { isEmptyString } from '../../util/Util';

/**
 * A function to get the default options for the time of a formatted date.
 * @return the options
 * @author agd
 */
export const getDefaultTimeFormattingOptions: () => Intl.DateTimeFormatOptions = () => ({
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  //for DateParser to work correctly this should not be h11, h12.
  //When you decide to change that make sure AM and PM parsing is supported.
  hourCycle: 'h23' // TODO: should depend on locale, no?
});

/**
 * A function to get the default options for the date of a formatted date.
 * @return the options
 * @author agd
 */
export const getDefaultDateFormattingOptions: () => Intl.DateTimeFormatOptions = () => ({
  year: 'numeric',
  month: '2-digit',
  day: '2-digit'
});

/**
 * DateFormatter class for formatting dates in a consistent format.
 * @author agd
 */
export class DateFormatter {
  /**
   * The locale to use for the formatter.
   */
  readonly locale: string;
  /**
   * {@link Intl.DateTimeFormatOptions} options for the used formatter.
   */
  readonly options: Intl.DateTimeFormatOptions;

  /**
   * Creates a new {@link DateFormatter}. The formatter will use an {@link Intl.DateTimeFormat} formatter.
   * If the locale is rm-CH (romansh), nl-NL (dutch) is used instead, as not all
   * browsers are supporting rm-CH. nl-NL is supported and has the same date format.
   *
   * @param options? {@link Intl.DateTimeFormatOptions} options for the used formatter.
   */
  constructor(options?: Intl.DateTimeFormatOptions | boolean) {
    this.locale = window.SenseConstants?.locale ?? 'en-US';
    if (this.locale === 'rm-CH') {
      this.locale = 'nl-NL';
    }

    // default options
    let opt: Intl.DateTimeFormatOptions = getDefaultDateFormattingOptions();
    // set the correct format options
    if (options) {
      if (typeof options === 'boolean' && options) {
        opt = {
          ...opt,
          ...getDefaultTimeFormattingOptions()
        };
      } else {
        // simply set the passed options
        opt = options;
      }
    }

    this.options = opt;
  }

  /**
   * Formats the given date using the formatter of this DateFormatter instance.
   * @param date the date to format. This can either be a string or a {@link Date} object.
   * @return the formatted date as string
   */
  formatDate(date: string | Date): string {
    if (date === undefined || date === null) {
      console.warn(
        'Unsupported undefined/null date passed to DateFormatter#formatDate. Returning empty string.'
      );
      return '';
    }

    if (typeof date === 'string') {
      date = this.readStringDate(date);
    }

    // If weekday, year, month and day are all undefined, then the
    // `toLocaleDateString` function uses 'numeric' as its default.
    // We assume that only the time is supposed to be displayed instead,
    // so `toLocaleTimeString` is used
    if (
      !this.options.weekday &&
      !this.options.year &&
      !this.options.month &&
      !this.options.day
    ) {
      return date.toLocaleTimeString(this.locale, this.options);
    }

    return date.toLocaleDateString(this.locale, this.options);
  }

  /**
   *
   * <ul>
   *  <li><b>if date is null, empty or undefined</b> -> new Date()</li>
   *  <li><b>if date is iso date only</b>, "2020-05-23" for example -> new Date(year, month, day) so the string is parsed and the date is returned with a sensible timezone matching the user's</li>
   *  <li><b>else</b> -> new Date(dateString)</li>
   * <ul>
   *
   * @param date {string}
   *
   * @return {Date}
   */
  readStringDate(date: string | undefined): Date {
    if (isEmptyString(date)) {
      return new Date();
    }

    if (this.isStringIsoLocalDate(date)) {
      return this.isoLocalDateStringToJsDate(date);
    }

    // eslint-disable-next-line ehs-react/suspiciousDateCreation
    return new Date(date);
  }

  /**
   * Parse a given string to validate whether is it matching a LocalDate in string format: "2020-05-23" for example
   *
   * @param date the string to parse
   */
  isStringIsoLocalDate(date: string | undefined): date is string {
    if (isEmptyString(date)) {
      return false;
    }
    if (date.length !== 10) {
      return false;
    }
    // LocaleDate:toString gives an iso formatted value: "2020-05-23" for example
    // check separator
    if (date[4] !== '-' || date[7] !== '-') {
      return false;
    }

    // check numbers
    const dateElements = date.split('-');
    if (dateElements.length != 3) {
      return false;
    }

    for (const numberString of dateElements) {
      if (isNaN(numberString)) {
        return false;
      }
    }

    return true;
  }

  /**
   *
   * @param date a given LocalDate string in ISO format: "2020-05-23" for example
   *
   * @return a javascript Date
   */
  private isoLocalDateStringToJsDate(date: string): Date {
    const dateComponents = date.split('-');
    return new Date(
      parseInt(dateComponents[0]),
      parseInt(dateComponents[1]) - 1,
      parseInt(dateComponents[2])
    );
  }
}

/**
 * The default {@link DateFormatter} instance.
 *
 * @author agd
 */
export const DefaultDateFormatter = new DateFormatter();

/**
 * The default {@link DateFormatter} instance which includes the time in the formatted date.
 *
 * @author agd
 */
export const DefaultDateTimeFormatter = new DateFormatter(true);

/**
 * The default {@link DateFormatter} instance for extracting only the time of the given date.
 *
 * @author agd
 */
export const DefaultTimeFormatter = new DateFormatter(
  getDefaultTimeFormattingOptions()
);

/**
 * Function for creating a {@link DateFormatter} with custom {@link Intl.DateTimeFormatOptions} options.
 *
 * @param options custom options for the formatter see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/DateTimeFormat
 * @author agd
 */
export const customDateFormatter = (
  options: Intl.DateTimeFormatOptions
): DateFormatter => new DateFormatter(options);

/**
 * Convert local date to UTC. If null or undefined is passed, it will return current datetime in UTC.
 *
 * @param date to be converted
 */
export const getDateInUTC = (date?: Date): Date => {
  if (!date) {
    date = new Date();
  }
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000);
};
